import { ArrowRight, FileText, Folder, Package } from "lucide-react";
import { Prop } from "@/components/Prop";
import { BudBadge, Slide } from "./Slide";

const ROLES = [
  {
    bud: 15,
    role: "Encrypt each piece",
    detail: "The key is the hash of the content, so the same file always encrypts to the same blob.",
  },
  {
    bud: 16,
    role: "Group blobs into directories",
    detail: "A deterministic MessagePack manifest that names its children and carries their keys.",
  },
  {
    bud: 17,
    role: "Handle big files and big folders",
    detail: "Chunked file manifests and fanout nodes, using the same node format.",
  },
  {
    bud: 18,
    role: "Give the root a name",
    detail: "An nhash for a permanent snapshot, or a nostr event for an address that can change.",
  },
];

export function IntroSlide({ onStart }: { onStart: () => void }) {
  return (
    <Slide
      title="Build a hashtree by hand"
      lede={
        <>
          Four draft BUDs let a client store an entire directory tree using nothing but ordinary Blossom blobs. Servers
          implement none of them. You are going to build one tree, one node at a time, and publish it for real.
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        {ROLES.map((entry) => (
          <div key={entry.bud} className="space-y-1.5 rounded-lg border bg-card p-3">
            <BudBadge bud={entry.bud} />
            <p className="text-sm font-medium">{entry.role}</p>
            <p className="text-xs text-muted-foreground">{entry.detail}</p>
          </div>
        ))}
      </div>

      <div className="space-y-2 rounded-lg border bg-muted/30 p-4">
        <p className="text-sm font-medium">The tree you will build</p>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Node icon={Folder} label="root" type={2} />
          <ArrowRight className="size-4 text-muted-foreground" />
          <Node icon={Folder} label="your folder" type={2} />
          <ArrowRight className="size-4 text-muted-foreground" />
          <Node icon={FileText} label="your file" type={1} altType={0} />
        </div>
        <p className="text-xs text-muted-foreground">
          Every node is encrypted, and every parent link carries its child's key. One root hash plus one root key opens
          the whole thing.
        </p>
      </div>

      <button
        type="button"
        onClick={onStart}
        className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
      >
        Start <ArrowRight className="size-4" />
      </button>
    </Slide>
  );
}

function Node({
  icon: Icon,
  label,
  type,
  altType,
}: {
  icon: typeof Folder;
  label: string;
  type: number;
  altType?: number;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border bg-background px-2.5 py-1.5">
      <Icon className="size-3.5 text-muted-foreground" />
      {label}
      <span className="text-[0.65rem] text-muted-foreground">
        <Prop name="t" value={type} />
        {altType !== undefined && (
          <>
            {" or "}
            <Prop name="t" value={altType} />
          </>
        )}
      </span>
    </span>
  );
}

export { Package };
